import {
    Box,
    Button,
    HStack,
    VStack,
    Text,
    Circle,
    Input,
    Textarea,
    useToast,
    Icon,
    Menu,
    MenuButton,
    MenuList,
    Portal,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { FaChevronLeft, FaChevronDown } from 'react-icons/fa';
import { useGetProfile } from '../hooks/useGetProfile';
import { useUpdateUserStatus, AwayPreset } from '../hooks/useUpdateUserStatus';
import { formatAwayUntil } from '../hooks/useGetMentionableUsers';
import DualMonthRangeCalendar from './DualMonthRangeCalendar';
import { MenuItemRow, SegmentedControl } from '../ui';

const PRESETS: { value: AwayPreset | 'custom'; label: string }[] = [
    { value: '30m', label: '30 minutes' },
    { value: '1h', label: '1 hour' },
    { value: '2h', label: '2 hours' },
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This week' },
    { value: 'custom', label: 'Custom' },
];

function startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function toLocalTimeValue(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function combineDateAndTime(day: Date, timeHHmm: string): Date {
    const [hStr, mStr] = timeHHmm.split(':');
    const hours = Number(hStr);
    const minutes = Number(mStr);
    const result = new Date(day);
    result.setHours(
        Number.isFinite(hours) ? hours : 0,
        Number.isFinite(minutes) ? minutes : 0,
        0,
        0,
    );
    return result;
}

function formatDayLabel(d: Date): string {
    return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/** Compact Available / Away switch shown on the root profile menu. */
export const PresenceStatusToggle = ({ onOpenAway }: { onOpenAway: () => void }) => {
    const toast = useToast();
    const { data: profile } = useGetProfile();
    const updateStatus = useUpdateUserStatus();
    const isAway = profile?.presenceStatus === 'away';

    const setAvailable = async () => {
        if (!isAway) return;
        try {
            await updateStatus.mutateAsync({ presenceStatus: 'available' });
            toast({ title: 'Status set to Available', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Failed to update status', status: 'error', duration: 3000 });
        }
    };

    return (
        <Box>
            <Text
                fontSize="11px"
                fontWeight="semibold"
                color="gray.500"
                mb={2}
                textTransform="uppercase"
                letterSpacing="0.04em"
            >
                Status
            </Text>

            {/* Same primitive as the /chats channel switcher — one toggle language. */}
            <SegmentedControl<'available' | 'away'>
                fullWidth
                value={isAway ? 'away' : 'available'}
                onChange={(next) => {
                    if (next === 'available') setAvailable();
                    else onOpenAway();
                }}
                items={[
                    {
                        value: 'available',
                        label: (
                            <HStack spacing={1.5} as="span" display="inline-flex">
                                <Circle size="7px" bg="green.400" />
                                <Box as="span">Available</Box>
                            </HStack>
                        ),
                    },
                    {
                        value: 'away',
                        label: (
                            <HStack spacing={1.5} as="span" display="inline-flex">
                                <Circle size="7px" bg="orange.400" />
                                <Box as="span">Away</Box>
                            </HStack>
                        ),
                    },
                ]}
            />

            {isAway && profile?.awayEndsAt && (
                <Text fontSize="xs" color="gray.500" mt={1.5} noOfLines={2}>
                    Until {formatAwayUntil(profile.awayEndsAt)}
                    {profile.statusNote ? ` · ${profile.statusNote}` : ''}
                </Text>
            )}
        </Box>
    );
};

/** Teams-style inset panel that replaces the profile switcher content. */
export const AwayStatusPanel = ({
    onBack,
    onDone,
    onCustomModeChange,
}: {
    onBack: () => void;
    onDone: () => void;
    /** Lets the parent widen the flyout when the dual-month calendar is shown. */
    onCustomModeChange?: (isCustom: boolean) => void;
}) => {
    const toast = useToast();
    const { data: profile } = useGetProfile();
    const updateStatus = useUpdateUserStatus();

    const today = useMemo(() => startOfDay(new Date()), []);
    const maxDate = useMemo(() => {
        const d = startOfDay(new Date());
        d.setFullYear(d.getFullYear() + 1);
        return d;
    }, []);

    const [selectedPreset, setSelectedPreset] = useState<AwayPreset | 'custom'>('1h');
    const [statusNote, setStatusNote] = useState(profile?.statusNote ?? '');
    const [rangeStart, setRangeStart] = useState(() => startOfDay(new Date()));
    const [rangeEnd, setRangeEnd] = useState(() => {
        const d = startOfDay(new Date());
        d.setDate(d.getDate() + 1);
        return d;
    });
    const [startTime, setStartTime] = useState(() => toLocalTimeValue(new Date()));
    const [endTime, setEndTime] = useState(() => {
        const end = new Date();
        end.setHours(end.getHours() + 2);
        return toLocalTimeValue(end);
    });

    const presetLabel =
        PRESETS.find(p => p.value === selectedPreset)?.label ?? '1 hour';

    const selectPreset = (value: AwayPreset | 'custom') => {
        setSelectedPreset(value);
        onCustomModeChange?.(value === 'custom');
    };

    const confirmAway = async () => {
        try {
            if (selectedPreset === 'custom') {
                const start = combineDateAndTime(rangeStart, startTime);
                const end = combineDateAndTime(rangeEnd, endTime);
                if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                    toast({ title: 'Enter a valid date and time', status: 'warning', duration: 3000 });
                    return;
                }
                if (end.getTime() <= start.getTime()) {
                    toast({ title: 'End must be after start', status: 'warning', duration: 3000 });
                    return;
                }
                if (end.getTime() <= Date.now()) {
                    toast({ title: 'End must be in the future', status: 'warning', duration: 3000 });
                    return;
                }
                await updateStatus.mutateAsync({
                    presenceStatus: 'away',
                    awayStartsAt: start.toISOString(),
                    awayEndsAt: end.toISOString(),
                    statusNote: statusNote.trim() || undefined,
                });
            } else {
                await updateStatus.mutateAsync({
                    presenceStatus: 'away',
                    preset: selectedPreset,
                    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
                    statusNote: statusNote.trim() || undefined,
                });
            }
            toast({ title: 'Status set to Away', status: 'success', duration: 2000 });
            onCustomModeChange?.(false);
            onDone();
        } catch {
            toast({ title: 'Failed to update status', status: 'error', duration: 3000 });
        }
    };

    const handleBack = () => {
        onCustomModeChange?.(false);
        onBack();
    };

    return (
        <VStack spacing={3} align="stretch">
            <Button
                variant="ghost"
                size="sm"
                justifyContent="flex-start"
                h="32px"
                px={1}
                fontWeight="medium"
                color="gray.700"
                leftIcon={<Icon as={FaChevronLeft as React.ElementType} w={3} h={3} />}
                onClick={handleBack}
                alignSelf="flex-start"
            >
                Back
            </Button>

            <Box borderBottomWidth="1px" borderColor="gray.200" mx={-4} />

            <Box>
                <Text fontSize="xs" color="gray.500" mb={1.5}>
                    Status
                </Text>
                <HStack
                    px={3}
                    h="36px"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="gray.50"
                    spacing={2}
                >
                    <Circle size="10px" bg="orange.400" />
                    <Text fontSize="sm" color="gray.800" flex={1}>
                        Away
                    </Text>
                </HStack>
            </Box>

            <Box>
                <Text fontSize="xs" color="gray.500" mb={1.5}>
                    Reset status after
                </Text>
                <Menu placement="bottom-start" strategy="fixed" gutter={4}>
                    <MenuButton
                        as={Button}
                        w="100%"
                        h="36px"
                        size="sm"
                        variant="outline"
                        borderColor={selectedPreset === 'custom' ? '#0071E3' : 'gray.200'}
                        fontWeight="normal"
                        textAlign="left"
                        rightIcon={<Icon as={FaChevronDown as React.ElementType} w={2.5} h={2.5} />}
                    >
                        {presetLabel}
                    </MenuButton>
                    <Portal>
                        <MenuList
                            zIndex={2100}
                            minW="240px"
                            p="6px"
                            boxShadow="none"
                            borderColor="gray.200"
                        >
                            {PRESETS.map(p => (
                                <MenuItemRow
                                    key={p.value}
                                    kind="single"
                                    isSelected={selectedPreset === p.value}
                                    onClick={() => selectPreset(p.value)}
                                >
                                    {p.label}
                                </MenuItemRow>
                            ))}
                        </MenuList>
                    </Portal>
                </Menu>

                {selectedPreset === 'custom' && (
                    <VStack mt={3} spacing={3} align="stretch">
                        <DualMonthRangeCalendar
                            startDate={rangeStart}
                            endDate={rangeEnd}
                            maxDate={maxDate}
                            initialLeftMonth={today}
                            onChange={(start, end) => {
                                setRangeStart(startOfDay(start));
                                setRangeEnd(startOfDay(end));
                            }}
                        />
                        <HStack spacing={3} align="flex-start">
                            <Box flex={1}>
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                    Start
                                </Text>
                                <Text fontSize="xs" color="gray.700" mb={1} noOfLines={1}>
                                    {formatDayLabel(rangeStart)}
                                </Text>
                                <Input
                                    type="time"
                                    size="sm"
                                    value={startTime}
                                    onChange={e => setStartTime(e.target.value)}
                                />
                            </Box>
                            <Box flex={1}>
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                    End
                                </Text>
                                <Text fontSize="xs" color="gray.700" mb={1} noOfLines={1}>
                                    {formatDayLabel(rangeEnd)}
                                </Text>
                                <Input
                                    type="time"
                                    size="sm"
                                    value={endTime}
                                    onChange={e => setEndTime(e.target.value)}
                                />
                            </Box>
                        </HStack>
                    </VStack>
                )}
            </Box>

            <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>
                    Note (optional)
                </Text>
                <Textarea
                    size="sm"
                    rows={2}
                    placeholder="e.g. in meetings"
                    value={statusNote}
                    onChange={e => setStatusNote(e.target.value)}
                    resize="none"
                />
            </Box>

            <HStack justify="flex-end" pt={1}>
                <Button
                    bg="#0071E3"
                    color="white"
                    _hover={{ bg: '#3141a5' }}
                    size="sm"
                    px={5}
                    onClick={confirmAway}
                    isLoading={updateStatus.isPending}
                >
                    Done
                </Button>
            </HStack>
        </VStack>
    );
};

/** @deprecated Prefer PresenceStatusToggle + AwayStatusPanel for inset navigation. */
const PresenceStatusMenu = ({ onOpenAway }: { onOpenAway?: () => void }) => (
    <PresenceStatusToggle onOpenAway={onOpenAway ?? (() => undefined)} />
);

export default PresenceStatusMenu;
